import React from "react";
import { Helmet } from "react-helmet";

const BRAND = process.env.REACT_APP_WEBSITE_TITLE || "R77";
const SITE_URL = "https://www.royal77world.com";

const DEFAULT_SEO = {
  title: `${BRAND} | Online Casino Malaysia`,
  description: `Explore live casino games, slots, sports betting, promotions and account features available to Malaysian players on ${BRAND}.`,
};

const PAGE_SEO = {
  "/": DEFAULT_SEO,
  "/online-live-games-malaysia": {
    title: `Live Casino Malaysia | ${BRAND}`,
    description: `Explore live casino games available to Malaysian players on ${BRAND}, including table game options designed for desktop and mobile play.`,
  },
  "/online-sports-games-malaysia": {
    title: `Sports Betting Malaysia | ${BRAND}`,
    description: `Explore sports betting markets available to Malaysian players on ${BRAND}, with event information and selections across popular sports.`,
  },
  "/e-sports": {
    title: `E-Sports Betting | ${BRAND}`,
    description: `View e-sports betting markets on ${BRAND}, with match selections and event information for competitive gaming titles in one place.`,
  },
  "/slot": {
    title: `Online Slots Malaysia | ${BRAND}`,
    description: `Browse online slot games available to Malaysian players on ${BRAND}, with a range of providers and titles for desktop and mobile play.`,
  },
  "/games": {
    title: `Online Games | ${BRAND}`,
    description: `Browse online games available on ${BRAND}, compare participating providers and choose a game category for desktop or mobile play.`,
  },
  "/p2p": {
    title: `P2P Games | ${BRAND}`,
    description: `Explore peer-to-peer games available on ${BRAND}, with participating providers and game options presented in one dedicated category.`,
  },
  "/lottery": {
    title: `Online Lottery | ${BRAND}`,
    description: `Explore online lottery games available on ${BRAND}, browse participating providers and review the available game options in one place.`,
  },
  "/speed": {
    title: `Speed Games | ${BRAND}`,
    description: `Browse speed games available on ${BRAND}, compare participating providers and select from the options offered for desktop and mobile play.`,
  },
  "/promos": {
    title: `Promotions | ${BRAND}`,
    description: `Browse current ${BRAND} promotions, review the eligibility requirements and understand the applicable terms before choosing an offer.`,
  },
  "/vip": {
    title: `VIP Programme | ${BRAND}`,
    description: `Learn how the ${BRAND} VIP Programme works, including its membership levels, available benefits and qualification requirements.`,
  },
  "/about-us": {
    title: `About ${BRAND}`,
    description: `Learn about ${BRAND}, the gaming categories available on the platform and the services provided to registered account holders.`,
  },
  "/info-centre": {
    title: `Info Centre | ${BRAND}`,
    description: `Visit the ${BRAND} Info Centre for guidance about accounts, payments, game categories, platform features and responsible play.`,
  },
  "/responsible-gaming": {
    title: `Responsible Gaming | ${BRAND}`,
    description: `Read ${BRAND}'s responsible gaming guidance, including practical account controls, safer play information and sources of additional support.`,
  },
  "/contact-us": {
    title: `Contact Us | ${BRAND}`,
    description: `Contact the ${BRAND} support team for help with your account, payments or platform questions through the available support channels.`,
  },
  "/certificate": {
    title: `Certificates | ${BRAND}`,
    description: `Review the certificates and regulatory information presented by ${BRAND}, including the supporting details published for platform visitors.`,
  },
  "/privacy-policy": {
    title: `Privacy Policy | ${BRAND}`,
    description: `Read the ${BRAND} Privacy Policy to understand how personal information is collected, used, protected and managed across the platform.`,
  },
  "/404": {
    title: `Page Not Found | ${BRAND}`,
    description: `The page you requested could not be found. Return to ${BRAND} or contact the support team if you need help finding information.`,
  },
};

const VENDOR_NAMES = {
  "all-we-can": "All We Can",
  "asia-gaming": "Asia Gaming",
  allbet: "AllBet",
  "dream-gaming": "Dream Gaming",
  "ea-gaming": "EA Gaming",
  "evolution-gaming": "Evolution Gaming",
  "gameplay-interactive": "GamePlay Interactive",
  "gold-diamond-gaming": "Gold Diamond Gaming",
  "i-soft-bet": "iSoftBet",
  live22: "Live22",
  "micro-gaming": "Microgaming",
  netent: "NetEnt",
  "play-n-go": "Play'n GO",
  playtech: "Playtech",
  "pocket-games-soft": "Pocket Games Soft",
  pop: "POP",
  "pragmatic-play": "Pragmatic Play",
  qtech: "QTech",
  "quick-spin": "Quickspin",
  saba: "SABA",
  "sa-gaming": "SA Gaming",
  sbobet: "SBOBet",
  "simple-play": "SimplePlay",
  "spade-gaming": "Spadegaming",
  "top-trend-gaming": "TopTrend Gaming",
};

const normalizePath = (pathname = "/") => {
  const path = pathname.split("?")[0].split("#")[0];
  return path.length > 1 ? path.replace(/\/+$/, "") : "/";
};

const VENDOR_PATH_PATTERN = /^\/(slot|speed|games|p2p|lottery)\/([^/]+)$/;

export const isIndexablePath = (pathname) => {
  const path = normalizePath(pathname);
  return (path !== "/404" && Boolean(PAGE_SEO[path])) || VENDOR_PATH_PATTERN.test(path);
};

export const getSeoForPath = (pathname) => {
  const path = normalizePath(pathname);
  if (PAGE_SEO[path]) {
    return PAGE_SEO[path];
  }

  const vendorMatch = path.match(VENDOR_PATH_PATTERN);
  if (vendorMatch) {
    const category = vendorMatch[1];
    const slug = vendorMatch[2].toLowerCase();
    const vendor = VENDOR_NAMES[slug] || slug
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    const categoryNames = {
      games: "Games",
      lottery: "Lottery Games",
      p2p: "P2P Games",
      slot: "Slots",
      speed: "Speed Games",
    };
    const categoryName = categoryNames[category];

    return {
      title: `${vendor} ${categoryName} | ${BRAND}`,
      description: `Explore ${vendor} ${categoryName.toLowerCase()} on ${BRAND}, with titles available for desktop and mobile play in the provider catalogue for Malaysian players.`,
    };
  }

  return DEFAULT_SEO;
};

export const getSeoHeadingForPath = (pathname) => {
  const path = normalizePath(pathname);

  // The download page owns a visible H1 in its desktop and mobile layouts.
  if (!isIndexablePath(path) || path === "/download-app") {
    return null;
  }

  if (path === "/") {
    return `${BRAND} Online Casino Malaysia`;
  }

  return getSeoForPath(path).title.split(" | ")[0];
};

const SeoMeta = ({
  title = DEFAULT_SEO.title,
  description = DEFAULT_SEO.description,
  pathname = "/",
  canonical,
  image,
  omitCanonical = false,
  noindex = false,
}) => {
  const canonicalUrl = canonical || `${SITE_URL}${normalizePath(pathname)}`;
  const imageUrl = image || `${SITE_URL}/logo/og-1200x630.jpg`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />
      {!omitCanonical && <link rel="canonical" href={canonicalUrl} />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={title} />
    </Helmet>
  );
};

export default SeoMeta;
